import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useFlags } from '../context/FlagsContext'

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [idx, setIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const nav = useNavigate()
  const { get } = useFlags()

  const items = useMemo(() => [
    { label: 'Home', to: '/' },
    { label: 'Upload', to: '/upload' },
    { label: 'Documents', to: '/documents' },
    { label: 'Analytics', to: '/analytics' },
    { label: 'Negotiation Checklist', to: '/negotiation-checklist' },
    { label: 'Document Timeline', to: '/document-timeline' },
    { label: 'What-Changed-If Analyzer', to: '/what-changed-if' },
    { label: 'Rate & Usage', to: '/usage' },
    { label: 'Advanced Search', to: '/search' },
    { label: 'Webhooks', to: '/webhooks' },
    { label: 'Diagnostics', to: '/diagnostics' },
  ], [])

  const filtered = useMemo(() => {
    const qq = q.toLowerCase().trim()
    return items.filter(i => i.label.toLowerCase().includes(qq))
  }, [q, items])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        if (!get('ui.commandPalette', true)) return
        e.preventDefault(); setOpen(true); setIdx(0)
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [get])

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0) }, [open])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i+1, filtered.length-1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i-1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); const it = filtered[idx]; if (it) { setOpen(false); nav(it.to) } }
  }

  const root = document.getElementById('portal-root') as HTMLElement
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[2000]"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/40"
            role="button"
            tabIndex={0}
            aria-label="Close command palette"
            onClick={() => setOpen(false)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                setOpen(false)
              }
            }}
          />
          <motion.div className="absolute top-[15vh] left-1/2 -translate-x-1/2 w-full max-w-xl rounded bg-white dark:bg-gray-900 shadow-elev-3 border"
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
          >
            <div className="p-3 border-b">
              <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKeyDown}
                placeholder="Search commands…" className="w-full h-10 px-3 rounded border bg-white dark:bg-gray-800 dark:border-gray-700" aria-label="Command search"
              />
            </div>
            <ul role="listbox" aria-label="Command results" className="max-h-[50vh] overflow-auto">
              {filtered.map((it, i) => (
                <li key={it.to}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === idx}
                    className={`w-full text-left px-3 py-2 cursor-pointer ${i === idx ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
                    onMouseEnter={() => setIdx(i)}
                    onFocus={() => setIdx(i)}
                    onClick={() => { setOpen(false); nav(it.to) }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setOpen(false)
                        nav(it.to)
                      }
                    }}
                  >
                    {it.label}
                  </button>
                </li>
              ))}
              {filtered.length === 0 && <li className="px-3 py-2 text-gray-500">No results</li>}
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    root
  )
}
